import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "../utils/dayjs";
import { /* TrendPreLoadLong, TrendPreLoadShort, */ Trends, TrendsWithRange, InfluxLoadRange, InfluxLoadStatus, InfluxQueryRange, InfluxHotpathBackfill } from "../actions/apiActions";
import { chartSeries as Colors } from "../../theme/muiTheme";

export const trendsDefaultState = {
  entries: {},
  formattedEntries: {},
  chartDataList: {
    chart1: [],
    chart2: [],
    chart3: [],
    chart4: [],
  },
  isTrendLoading: {
    loading: false,
    filter: "",
  },
  measurementMinMaxRange: {
    chart1: [],
    chart2: [],
    chart3: [],
    chart4: [],
  },
  diagnosticsMinMaxRange: {
    chart1: [],
    chart2: [],
    chart3: [],
    chart4: [],
  },
  // TrendUI state (formerly in TrendUIContext)
  selectedDateTimeFilter: "Live",
  timeFilterFrom: -1,
  timeFilterTo: 0,
  directTimestampFrom: null,
  directTimestampTo: null,
  devicesFilterList: {
    chart1: {},
    chart2: {},
    chart3: {},
    chart4: {},
  },
  devicesLiveList: {},
  // New InfluxDB data loading state
  dataLoadingStatus: {
    isLoading: false,
    loadProgress: 0,
    writeProgress: 0,
    status: null,
    totalBlobs: 0,
    loadedBlobs: 0,
    blobsWritten: 0,
    totalPoints: 0,
    writtenPoints: 0,
    elapsedSeconds: 0,
    loadedMonths: 0,
    maxMonths: 12,
    loadId: null,
    error: null,
    message: null,
    loading_type: null,
    gaps_total: 0,
    gaps_processed: 0,
    availableDataRange: {
      from: null,
      to: null,
      sources: []
    },
  },
};

const formattedEntries = (payload) => {
  const { hwId, valueName } = payload;
  const values = payload.historyData;
  const tempValues = [];
  values &&
    values.forEach((value) => {
      const parsedTimestamp = parseTimestamp(value.sourceTimestamp);
      const momentObj = dayjs(parsedTimestamp);
      const item = {
        hwId,
        dateTime: parsedTimestamp,
        seconds: momentObj.valueOf(), // Use dayjs's valueOf() for consistent timestamp
        valueName,
        measurementValue: value.measurementValue,
      };
      tempValues.push(item);
    });
  const lastData = {
    hwId,
    valueName,
    historyData: {
      valueName,
      hwId,
      [valueName]: tempValues,
    },
  };
  return lastData;
};

// Returns the chartKey where the HwId and ValueName are found
function getChartKey(HwId, ValueName, state) {
  for (const chartKey in state) {
    const chart = state[chartKey];

    if (chart.hasOwnProperty(HwId)) {
      const hwIdObject = chart[HwId];

      if (hwIdObject.hasOwnProperty(ValueName)) {
        return chartKey;
      }
    }
  }
  // Return null or undefined if not found
  return null;
}

// Handles both Unix timestamps and ISO 8601 timestamps
function parseTimestamp(timestamp) {
  if (!isNaN(timestamp)) {
    return dayjs.unix(timestamp).local().format();
  }
  return dayjs.utc(timestamp).local().format();
}

// Helper function to calculate days difference between two slider values
function calculateDateRangeInDays(minVal, maxVal, selectedDateTimeFilter) {
  const fromDayjs = dayjs().subtract(Math.abs(minVal), selectedDateTimeFilter);
  const toDayjs = dayjs().subtract(Math.abs(maxVal), selectedDateTimeFilter);
  return toDayjs.diff(fromDayjs, 'days');
}

// Helper function to calculate months needed for data loading
function calculateMonthsForDataLoading(minVal, maxVal, selectedDateTimeFilter) {
  const fromDayjs = dayjs().subtract(Math.abs(minVal), selectedDateTimeFilter);
  const toDayjs = dayjs().subtract(Math.abs(maxVal), selectedDateTimeFilter);
  const monthsDiff = Math.ceil(toDayjs.diff(fromDayjs, 'months', true));
  return Math.min(monthsDiff, 12); // Max 12 months as per requirement
}

const generateChartDataList = (payload, chart, chartsList) => {
  const { valueName, hwId, timespan } = payload;
  const formattedState = formattedEntries(payload);
  const charts = (chartsList && chartsList.length > 0 && [...chartsList]) || [];
  if (timespan !== "Live") {
    // History mode: replace data for this valueName/hwId
    const newValue = {
      valueName,
      hwId,
      data: formattedState.historyData[valueName],
    };
    const index = charts.findIndex((o) => o.valueName === valueName && o.hwId === hwId);
    if (index >= 0) {
      charts.splice(index, 1, newValue);
    } else {
      charts.push(newValue);
    }
    return charts;
  } else {
    // Live mode: only keep last 30 minutes, deduplicate
    const payloadFormatted = formattedEntries(payload);
    const index = charts.findIndex((o) => o.valueName === valueName && o.hwId === hwId);
    const lastHalfHour = dayjs().subtract(30, "minutes").valueOf();
    // Get new live data points (from payload)
    const newLivePoints = payloadFormatted.historyData[valueName].filter(o => o.seconds >= lastHalfHour);
    if (index >= 0) {
      // Merge with existing, but only keep last 30 minutes and deduplicate by timestamp
      const existingPoints = charts[index].data.filter(o => o.seconds >= lastHalfHour);
      // Deduplicate: use a Map keyed by seconds
      const allPoints = [...existingPoints, ...newLivePoints];
      const deduped = Array.from(
        new Map(allPoints.map(p => [p.seconds, p])).values()
      ).sort((a, b) => a.seconds - b.seconds);
      const newValue = {
        valueName,
        hwId,
        data: deduped,
      };
      charts.splice(index, 1, newValue);
    } else {
      // No existing data, just use new live points
      const newValue = {
        valueName,
        hwId,
        data: newLivePoints,
      };
      charts.push(newValue);
    }
    return charts;
  }
};


export const fetchLiveTrends = createAsyncThunk(
  "trends/fetchLiveTrends",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, param, filter, chart, signal } = data;
    const { config } = getState().config[systemId].system;
    if (config && config.DATA_URL) {
      try {
        param.SiteId = config.CUSTOMER_ID;
        param.EdgeUnitId = config.DEVICE_ID;
        const response = await Trends.get(config.DATA_URL, param, filter, { signal });
        return { systemId, response, chart };
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response.status || 'unknown' };
        // rejectWithValue is handled in case: fetchTrends.rejected
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    }
  }
);

export const fetchTrendsWithRange = createAsyncThunk(
  "trends/fetchTrendsWithRange",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, param, filter, chart, signal } = data;
    const { config } = getState().config[systemId].system;
    if (config && config.OS_ANALYTICS_URL) {
      try {
        param.SiteId = config.CUSTOMER_ID;
        param.EdgeUnitId = config.DEVICE_ID;
        const response = await TrendsWithRange.get(config.OS_ANALYTICS_URL, param, filter, { signal });
        return { systemId, response, chart };
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response.status || 'unknown' };
        // rejectWithValue is handled in case: fetchTrendsWithRange.rejected
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    }
  }
);

// DISABLED - Old preload endpoints no longer used with new InfluxDB integration
// export const triggerTrendShortPreLoad = createAsyncThunk(
//   "trends/triggerTrendShortPreLoad",
//   async (data, { getState }) => {
//     const { systemId, SiteId, DeviceId } = data;
//     const { config } = getState().config[systemId].system;
//     const param = { SiteId, EdgeUnitId: DeviceId } || {};
//     const response = await TrendPreLoadShort.post(config.OS_ANALYTICS_URL, param);
//     return { systemId, response };
//   }
// );

// export const triggerTrendLongPreLoad = createAsyncThunk(
//   "trends/triggerTrendLongPreLoad",
//   async (data, { getState }) => {
//     const { systemId, DeviceId } = data;
//     const { config } = getState().config[systemId].system;
//     const param = { SiteId: config.CUSTOMER_ID, EdgeUnitId: DeviceId } || {};
//     const response = await TrendPreLoadLong.post(config.OS_ANALYTICS_URL, param);
//     return { systemId, response };
//   }
// );

// New InfluxDB async thunks
export const loadInfluxData = createAsyncThunk(
  "trends/loadInfluxData",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, body } = data;
    const { config } = getState().config[systemId].system;

    // Only load data in cloud environment
    if (config && config.OS_ANALYTICS_URL && config.APP_ENV === 'Cloud') {
      try {
        const response = await InfluxLoadRange.post(config.OS_ANALYTICS_URL, body);
        return { systemId, response, body };
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response.status || 'unknown' };
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    } else {
      // In edge environment, return immediate success (no loading needed)
      return {
        systemId,
        response: {
          status: 'not_applicable',
          message: 'Data loading not needed in edge environment'
        },
        body
      };
    }
  }
);

export const checkInfluxLoadStatus = createAsyncThunk(
  "trends/checkInfluxLoadStatus",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, body } = data;
    const { config } = getState().config[systemId].system;

    // Only check status in cloud environment
    if (config && config.OS_ANALYTICS_URL && config.APP_ENV === 'Cloud') {
      try {
        const response = await InfluxLoadStatus.get(config.OS_ANALYTICS_URL, body);
        return { systemId, response };
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response.status || 'unknown' };
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    } else {
      // In edge environment, return empty status (no loading needed)
      return {
        systemId,
        response: {
          status: 'not_applicable',
          message: 'Status check not needed in edge environment'
        }
      };
    }
  }
);

export const triggerHotpathBackfill = createAsyncThunk(
  "trends/triggerHotpathBackfill",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, body } = data;
    const { config } = getState().config[systemId].system;

    if (config && config.OS_ANALYTICS_URL && config.APP_ENV === 'Cloud') {
      try {
        const response = await InfluxHotpathBackfill.post(config.OS_ANALYTICS_URL, body);
        return { systemId, response, body };
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response?.status || 'unknown' };
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    } else {
      return {
        systemId,
        response: {
          status: 'not_applicable',
          message: 'Hotpath backfill not available in edge environment'
        },
        body
      };
    }
  }
);

export const fetchUnifiedTrends = createAsyncThunk(
  "trends/fetchUnifiedTrends",
  async (data, { getState, rejectWithValue }) => {
    const { systemId, param, filter, chart, signal, minVal, maxVal, selectedDateTimeFilter } = data;
    const { config } = getState().config[systemId].system;

    if (config) {
      try {
        param.SiteId = config.CUSTOMER_ID;
        param.EdgeUnitId = config.DEVICE_ID;
        // Environment-specific endpoint selection
        if (config.APP_ENV === 'Cloud') {
          // Use InfluxDB endpoint for cloud
          const baseUrl = config.OS_ANALYTICS_URL || config.DATA_URL;
          const response = await InfluxQueryRange.get(baseUrl, param, { signal });
          return { systemId, response, chart };
        } else {
          // Edge environment: Use original trendrange endpoint (no data loading needed)
          const baseUrl = config.OS_ANALYTICS_URL || config.DATA_URL;
          const response = await TrendsWithRange.get(baseUrl, param, { signal });
          return { systemId, response, chart };
        }
      } catch (e) {
        const errorInfo = { message: e.message, status: e.response.status || 'unknown' };
        throw rejectWithValue({ systemId, error: errorInfo });
      }
    }
  }
);

const trendsSlice = createSlice({
  name: "trends",
  initialState: trendsDefaultState,
  reducers: {
    clearAllChartData(state, action) {
      state.chartDataList = {
        chart1: [],
        chart2: [],
        chart3: [],
        chart4: [],
      };
    },
    setYAxisForMeasurement(state, action) {
      const { chart, data } = action.payload; // chart specifies which chart to update
      if (state.measurementMinMaxRange[chart]) {
        state.measurementMinMaxRange[chart] = data;
      }
    },
    setYAxisForDiagnostics(state, action) {
      const { chart, data } = action.payload; // chart specifies which chart to update
      if (state.diagnosticsMinMaxRange[chart]) {
        state.diagnosticsMinMaxRange[chart] = data;
      }
    },
    removeChartLine(state, action) {
      const chart = action.payload.selectedChart;
      const newChartDataList = state.chartDataList[chart].filter((item) => {
        const itemToKeep =
          item.valueName !== action.payload.valueName || item.hwId !== action.payload.hwId;
        return itemToKeep;
      });
      state.chartDataList[chart] = newChartDataList;
    },
    /** Append a single live data point to the appropriate chart (called from TrendContainer). */
    appendLiveChartData(state, action) {
      const { dataObject, chart } = action.payload;
      const item = {
        hwId: dataObject.HwId,
        timespan: "Live",
        valueName: dataObject.ValueName,
        historyData: [
          {
            sourceTimestamp: parseTimestamp(dataObject.ValueTimeStamp),
            measurementValue: dataObject.Value,
          },
        ],
      };
      state.entries = item;
      state.formattedEntries = formattedEntries(item);
      if (state.chartDataList[chart]) {
        state.chartDataList[chart] = generateChartDataList(item, chart, [
          ...state.chartDataList[chart],
        ]);
      }
    },
    resetDataLoadingStatus(state) {
      state.dataLoadingStatus = {
        isLoading: false,
        loadProgress: 0,
        writeProgress: 0,
        status: null,
        totalBlobs: 0,
        loadedBlobs: 0,
        blobsWritten: 0,
        totalPoints: 0,
        writtenPoints: 0,
        elapsedSeconds: 0,
        loadedMonths: 0,
        maxMonths: 12,
        loadId: null,
        error: null,
        message: null,
        loading_type: null,
        gaps_total: 0,
        gaps_processed: 0,
        availableDataRange: {
          from: null,
          to: null,
          sources: []
        },
      };
    },
    updateDataLoadingProgress(state, action) {
      state.dataLoadingStatus.loadProgress = action.payload.progress;
      state.dataLoadingStatus.isLoading = action.payload.progress < 100;
    },
    // ─── TrendUI reducers (formerly in TrendUIContext) ─────────────────────────
    clearFilterList(state) {
      state.devicesFilterList = { chart1: {}, chart2: {}, chart3: {}, chart4: {} };
    },
    /** Rehydrates chart selections persisted in localStorage (see trendSelectionsStorage). */
    restoreDevicesFilterList(state, action) {
      const { devicesFilterList } = action.payload;
      if (!devicesFilterList) return;
      state.devicesFilterList = {
        chart1: {},
        chart2: {},
        chart3: {},
        chart4: {},
        ...devicesFilterList,
      };
    },
    setDateTimeFilter(state, action) {
      const { data, minVal, maxVal, directTimestampFrom, directTimestampTo } = action.payload;
      state.selectedDateTimeFilter = data;
      if (minVal !== undefined) state.timeFilterFrom = minVal;
      if (maxVal !== undefined) state.timeFilterTo = maxVal;
      if (directTimestampFrom !== undefined) state.directTimestampFrom = directTimestampFrom;
      if (directTimestampTo !== undefined) state.directTimestampTo = directTimestampTo;
      if (minVal !== undefined && directTimestampFrom === undefined) {
        state.directTimestampFrom = null;
        state.directTimestampTo = null;
      }
    },
    setAllLoadingTrue(state) {
      for (const chart of Object.keys(state.devicesFilterList)) {
        for (const hwId of Object.keys(state.devicesFilterList[chart])) {
          for (const vn of Object.keys(state.devicesFilterList[chart][hwId])) {
            state.devicesFilterList[chart][hwId][vn].loading = true;
          }
        }
      }
    },
    setDevicesFilter(state, action) {
      const { selectedItem, hwId, flag, unit, selectedChart } = action.payload;
      const { valueName } = selectedItem;

      // Reuse the color already assigned to this measurement on another chart,
      // so its selection badge and line color stay the same across all charts.
      let color;
      for (const chart of Object.keys(state.devicesFilterList)) {
        const existing = state.devicesFilterList[chart]?.[hwId]?.[valueName];
        if (existing?.color) {
          color = existing.color;
          break;
        }
      }

      if (!color) {
        const target = state.devicesFilterList[selectedChart] || {};
        const usedColors = new Set();
        let usedCount = 0;
        Object.values(target).forEach((item) =>
          Object.values(item).forEach((p) => {
            usedColors.add(p.color);
            usedCount += 1;
          })
        );
        const available = Colors.filter((c) => !usedColors.has(c));
        // Pick colors in fixed array order (no randomness): first unused color,
        // or once all are used, wrap back around to the start in the same order.
        color = available.length > 0
          ? available[0]
          : Colors[usedCount % Colors.length];
      }
      if (!state.devicesFilterList[selectedChart]) state.devicesFilterList[selectedChart] = {};
      if (!state.devicesFilterList[selectedChart][hwId]) state.devicesFilterList[selectedChart][hwId] = {};
      state.devicesFilterList[selectedChart][hwId][valueName] = {
        ...selectedItem, flag, selectedChart, isChecked: true, color, unit, loading: true,
      };
    },
    removeDeviceFilter(state, action) {
      const { hwId, selectedItem, selectedChart } = action.payload;
      if (state.devicesFilterList[selectedChart]?.[hwId]) {
        delete state.devicesFilterList[selectedChart][hwId][selectedItem.valueName];
        if (Object.keys(state.devicesFilterList[selectedChart][hwId]).length === 0) {
          delete state.devicesFilterList[selectedChart][hwId];
        }
      }
    },
    removeDeviceFilterList(state, action) {
      const { hwId, valueName } = action.payload;
      if (state.devicesLiveList[hwId]?.[valueName]) {
        delete state.devicesLiveList[hwId][valueName];
      }
    },
    setFetchPending(state, action) {
      const { chart, HwId, ValueName } = action.payload;
      if (state.devicesFilterList[chart]?.[HwId]?.[ValueName]) {
        state.devicesFilterList[chart][HwId][ValueName].loading = true;
      }
    },
    setFetchFulfilled(state, action) {
      const { chart, HwId, ValueName, response } = action.payload;
      const item = state.devicesFilterList[chart]?.[HwId]?.[ValueName];
      if (!item) return;
      item.fetchErrorMsg = "";
      item.fetchErrorStatus = "";
      item.loading = false;
      if (state.selectedDateTimeFilter === "Live" && response) {
        if (!state.devicesLiveList[response.hwId]?.[response.valueName]) {
          if (!state.devicesLiveList[response.hwId]) state.devicesLiveList[response.hwId] = {};
          state.devicesLiveList[response.hwId][response.valueName] = response;
        }
      }
    },
    setFetchRejected(state, action) {
      const { chart, HwId, ValueName, errorMessage, httpResponseStatus, requiresLoading } = action.payload;
      const item = state.devicesFilterList[chart]?.[HwId]?.[ValueName];
      if (!item) return;
      item.fetchErrorMsg = errorMessage;
      item.fetchErrorStatus = httpResponseStatus;
      item.loading = false;
      item.requiresDataLoading = requiresLoading || false;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchLiveTrends.pending, (state, action) => {
        state.isTrendLoading.loading = true;
      })
      .addCase(fetchLiveTrends.fulfilled, (state, action) => {
        state.isTrendLoading.loading = false;
        const result = action.payload.response;
        if (result === "") {
          // status "204 No Content" leads to result beign empty. In this case we do not want to update the state to avoid crashing the app
          return;
        }
        state.entries = result;
        const chart = action.payload.chart;
        state.chartDataList[chart] = generateChartDataList(result, chart, [
          ...state.chartDataList[chart],
        ]);
      })
      .addCase(fetchLiveTrends.rejected, (state, action) => {
        const param = action.meta?.arg?.param;
        if (param) {
          const { HwId, ValueName } = param;
          const chartKey = action.meta.arg.chart;
          if (chartKey) {
            // Filter out the specific entry that matches HwId and ValueName
            const filteredChartData = state.chartDataList[chartKey].filter(item =>
              !(item.hwId === HwId && item.valueName === ValueName)
            );
            // Update the state with the filtered data
            state.chartDataList[chartKey] = filteredChartData;
          }
        }
        // Handle any specific logging or error responses
        const errorMessage = action.payload?.error?.message;
        const httpResponseStatus = action.payload?.error?.status;
        if (errorMessage) {
          console.error(`Error fetching trends: ${errorMessage} (Status: ${httpResponseStatus})`);
        }
      })
      .addCase(fetchTrendsWithRange.pending, (state, action) => {
        state.isTrendLoading.loading = true;
      })
      .addCase(fetchTrendsWithRange.fulfilled, (state, action) => {
        state.isTrendLoading.loading = false;
        const result = action.payload.response;
        if (result === "") {
          // status "204 No Content" leads to result beign empty. In this case we do not want to update the state to avoid crashing the app
          return;
        }
        state.entries = result;
        const chart = action.payload.chart;
        state.chartDataList[chart] = generateChartDataList(result, chart, [
          ...state.chartDataList[chart],
        ]);
      })
      .addCase(fetchTrendsWithRange.rejected, (state, action) => {
        const param = action.meta?.arg?.param;
        if (param) {
          const { HwId, ValueName } = param;
          const chartKey = action.meta.arg.chart;
          if (chartKey) {
            // Filter out the specific entry that matches HwId and ValueName
            const filteredChartData = state.chartDataList[chartKey].filter(item =>
              !(item.hwId === HwId && item.valueName === ValueName)
            );
            // Update the state with the filtered data
            state.chartDataList[chartKey] = filteredChartData;
          }
        }
        // Handle any specific logging or error responses
        const errorMessage = action.payload?.error?.message;
        const httpResponseStatus = action.payload?.error?.status;
        if (errorMessage) {
          console.error(`Error fetching trends: ${errorMessage} (Status: ${httpResponseStatus})`);
        }
      })
      // DISABLED - Old preload endpoints no longer used
      // .addCase(triggerTrendShortPreLoad.fulfilled, (state, action) => {
      //   console.log("SHORT PRE LOAD FOR TRENDS");
      // })
      // .addCase(triggerTrendLongPreLoad.fulfilled, (state, action) => {
      //   console.log("LONG PRE LOAD FOR TRENDS");
      // })

      // New InfluxDB reducer cases
      .addCase(loadInfluxData.pending, (state, action) => {
        state.dataLoadingStatus.isLoading = false;
        state.dataLoadingStatus.loadProgress = 0;
        state.dataLoadingStatus.error = null;
        state.dataLoadingStatus.loadedMonths = action.meta.arg.body?.MonthsToLoad || 0;
      })
      .addCase(loadInfluxData.fulfilled, (state, action) => {
        state.dataLoadingStatus.loadId = action.payload.response.loadId;
        state.dataLoadingStatus.isLoading = true;

        // Loading status will be updated by checkInfluxLoadStatus polling
      })
      .addCase(loadInfluxData.rejected, (state, action) => {
        state.dataLoadingStatus.isLoading = false;
        state.dataLoadingStatus.error = action.payload?.error?.message || 'Failed to start data loading';
      })

      .addCase(checkInfluxLoadStatus.fulfilled, (state, action) => {
        const { response } = action.payload;
        console.log('checkInfluxLoadStatus response:', response); // Debug logging

        if (response.status === 'not_found' || response.status === 'notfound') {
          // No active loading operation found
          state.dataLoadingStatus.isLoading = false;
          state.dataLoadingStatus.loadProgress = 0;
          state.dataLoadingStatus.writeProgress = 0;
          state.dataLoadingStatus.status = null;
          state.dataLoadingStatus.error = null;
          state.dataLoadingStatus.message = response.message || null;
          // Update available data range if provided
          if (response.available_data_range) {
            state.dataLoadingStatus.availableDataRange.from = response.available_data_range.from;
            state.dataLoadingStatus.availableDataRange.to = response.available_data_range.to;
            state.dataLoadingStatus.availableDataRange.sources = response.available_data_range.sources || [];
          }
        } else if (response.status === 'completed' || (response.load_percent >= 100 && response.write_percent >= 100)) {
          state.dataLoadingStatus.isLoading = false;
          state.dataLoadingStatus.loadProgress = 100;
          state.dataLoadingStatus.writeProgress = 100;
          state.dataLoadingStatus.status = 'completed';
          state.dataLoadingStatus.message = response.message || null;
          // Update available data range if provided
          if (response.available_data_range) {
            state.dataLoadingStatus.availableDataRange.from = response.available_data_range.from;
            state.dataLoadingStatus.availableDataRange.to = response.available_data_range.to;
            state.dataLoadingStatus.availableDataRange.sources = response.available_data_range.sources || [];
          }
        } else if (response.status === 'failed' || response.status === 'error' || response.error) {
          state.dataLoadingStatus.isLoading = false;
          state.dataLoadingStatus.error = response.error || response.message || 'Data loading failed';
          state.dataLoadingStatus.status = 'error';
        } else {
          // Active loading or writing operation
          state.dataLoadingStatus.isLoading = true;
          state.dataLoadingStatus.status = response.status || 'loading';
          state.dataLoadingStatus.loadProgress = Math.min(response.load_percent || 0, 100);
          state.dataLoadingStatus.writeProgress = Math.min(response.write_percent || 0, 100);
          state.dataLoadingStatus.totalBlobs = response.total_blobs || 0;
          state.dataLoadingStatus.loadedBlobs = response.loaded_blobs || 0;
          state.dataLoadingStatus.blobsWritten = response.blobs_written || 0;
          state.dataLoadingStatus.totalPoints = response.total_points || 0;
          state.dataLoadingStatus.writtenPoints = response.written_points || 0;
          state.dataLoadingStatus.elapsedSeconds = response.elapsed_seconds || 0;
          state.dataLoadingStatus.message = response.message;
          // New fields from updated API response
          state.dataLoadingStatus.loading_type = response.loading_type || null;
          state.dataLoadingStatus.gaps_total = response.gaps_total || 0;
          state.dataLoadingStatus.gaps_processed = response.gaps_processed || 0;
          // Update available data range if provided
          if (response.available_data_range) {
            state.dataLoadingStatus.availableDataRange.from = response.available_data_range.from;
            state.dataLoadingStatus.availableDataRange.to = response.available_data_range.to;
            state.dataLoadingStatus.availableDataRange.sources = response.available_data_range.sources || [];
          }
        }
      })
      .addCase(checkInfluxLoadStatus.rejected, (state, action) => {
        state.dataLoadingStatus.error = action.payload?.error?.message || 'Failed to check loading status';
      })

      // Hotpath Backfill reducer cases
      .addCase(triggerHotpathBackfill.pending, (state, action) => {
        state.dataLoadingStatus.isLoading = true;
        state.dataLoadingStatus.error = null;
        state.dataLoadingStatus.message = 'Starting hotpath backfill...';
      })
      .addCase(triggerHotpathBackfill.fulfilled, (state, action) => {
        state.dataLoadingStatus.isLoading = true; // Keep loading true - will be updated by status polling
        state.dataLoadingStatus.message = action.payload.response?.message || 'Hotpath backfill initiated';
      })
      .addCase(triggerHotpathBackfill.rejected, (state, action) => {
        state.dataLoadingStatus.isLoading = false;
        state.dataLoadingStatus.error = action.payload?.error?.message || 'Failed to trigger hotpath backfill';
      })

      .addCase(fetchUnifiedTrends.pending, (state, action) => {
        state.isTrendLoading.loading = true;
      })
      .addCase(fetchUnifiedTrends.fulfilled, (state, action) => {
        state.isTrendLoading.loading = false;
        const result = action.payload.response;
        if (result === "") {
          return;
        }
        state.entries = result;
        const chart = action.payload.chart;
        state.chartDataList[chart] = generateChartDataList(result, chart, [
          ...state.chartDataList[chart],
        ]);
      })
      .addCase(fetchUnifiedTrends.rejected, (state, action) => {
        state.isTrendLoading.loading = false;
        const param = action.meta?.arg?.param;
        if (param) {
          const { HwId, ValueName } = param;
          const chartKey = action.meta.arg.chart;
          if (chartKey) {
            const filteredChartData = state.chartDataList[chartKey].filter(item =>
              !(item.hwId === HwId && item.valueName === ValueName)
            );
            state.chartDataList[chartKey] = filteredChartData;
          }
        }
        const errorMessage = action.payload?.error?.message;
        const httpResponseStatus = action.payload?.error?.status;
        if (errorMessage) {
          console.error(`Error fetching unified trends: ${errorMessage} (Status: ${httpResponseStatus})`);
        }
      });
  }
});

export const {
  clearAllChartData,
  setYAxisForMeasurement,
  setYAxisForDiagnostics,
  removeChartLine,
  appendLiveChartData,
  resetDataLoadingStatus,
  updateDataLoadingProgress,
  clearFilterList,
  restoreDevicesFilterList,
  setDateTimeFilter,
  setAllLoadingTrue,
  setDevicesFilter,
  removeDeviceFilter,
  removeDeviceFilterList,
  setFetchPending,
  setFetchFulfilled,
  setFetchRejected,
} = trendsSlice.actions;

/** Exported for TrendContainer live-data hook */
export { getChartKey };

export default trendsSlice.reducer;
